import {TranslatableResourceType} from 'types';
import {parseGidWithParams, parseGidType} from '@web-utilities/other';
import {
  Box,
  InlineStack,
  Thumbnail,
  BlockStack,
  Text,
  UnstyledButton,
} from '@shopify/polaris';
import snakeCase from 'lodash/snakeCase';
import {useShopLocalesAndMarkets} from 'utilities/use-shop-locales-and-markets';
import {useNavigate} from 'react-router-dom';
import {getSearchFromQuery, type AppQuery} from 'utilities/url';
import classNames from 'classnames';
import {useSearch} from 'features/Translations/hooks';

import {SearchResult as SearchResultType} from '../../types';
import {isUnsearchableThemeResourceType} from '../../../../utils';

import styles from './GroupedSearchResult.scss';

interface Props {
  groupedResults: SearchResultType[];
  onResultClick: () => void;
}

export const GroupedSearchResult = ({groupedResults, onResultClick}: Props) => {
  const {setSelected, selected} = useSearch();
  const {currentLocale, primaryLocale, currentMarket} =
    useShopLocalesAndMarkets();
  const navigate = useNavigate();

  const firstResult = groupedResults[0];
  const resultResourceId =
    firstResult.translatableParentResourceId || firstResult.resourceId;
  const resourceType = snakeCase(
    parseGidType(resultResourceId),
  ).toUpperCase() as TranslatableResourceType;

  // isUnsearchableThemeResource refers to not using the Navigation.graphql query in the sidebar
  const isUnsearchableThemeResource =
    isUnsearchableThemeResourceType(resourceType);

  const lowerCaseResourceType = resourceType.toLowerCase();
  const {id, params: idParams} = parseGidWithParams(resultResourceId);
  const {displayTitle, displayImage} = firstResult;

  const handleResultClick = ({
    highlight,
    shopLocale,
    market,
    item,
  }: AppQuery) => {
    onResultClick();
    navigate(
      {
        pathname: `/localize/${lowerCaseResourceType}`,
        search: getSearchFromQuery({
          id,
          shopLocale,
          market,
          highlight,
          item,
          ...idParams,
        }),
      },
      {replace: true},
    );
  };

  const ids: string[] = [];

  const results = groupedResults.map((result) =>
    result.content.map((content) => {
      const shopLocale = content.shopLocale;
      const market = content.market;
      const isDefaultContent =
        !market?.handle && shopLocale.locale === primaryLocale?.locale;

      const contextParams = new URLSearchParams({
        locale: shopLocale.locale,
        market: market?.id || '',
      });

      const id = `${result.resourceId}/${
        result.key
      }/${contextParams.toString()}`;

      ids.push(id);

      const activeResult = selected?.includes(id);

      const shopLocaleToSet = isDefaultContent
        ? currentLocale?.locale
        : shopLocale.locale;

      return (
        <UnstyledButton
          key={id}
          className={classNames(
            styles.ContextualResultContainer,
            styles.Button,
            activeResult && styles.ActiveResult,
          )}
          onClick={() => {
            setSelected([id]);
            handleResultClick({
              highlight: result.key,
              shopLocale: shopLocaleToSet,
              market: market?.handle || currentMarket?.handle,
              ...(isUnsearchableThemeResource
                ? {item: result.key.split('.')[0]}
                : {}),
            });
          }}
        >
          <div className={styles.ContextualResult}>
            <Box
              paddingBlockStart="100"
              paddingBlockEnd="100"
              paddingInlineStart="300"
              paddingInlineEnd="300"
            >
              <Text as="span" variant="bodyMd" tone="subdued">
                <div
                  dangerouslySetInnerHTML={{
                    __html: content.highlights[0]?.replace(
                      /(<\/?(?:em)[^>]*>)|<[^>]+>/gi,
                      '$1',
                    ),
                  }}
                />
              </Text>
            </Box>
          </div>
        </UnstyledButton>
      );
    }),
  );

  return (
    <Box borderBlockEndWidth="025" borderColor="border-secondary">
      <Box
        paddingBlockStart="200"
        paddingBlockEnd="200"
        paddingInlineStart="300"
        paddingInlineEnd="300"
      >
        <BlockStack gap="100">
          <InlineStack gap="150" wrap={false} blockAlign="center">
            {displayImage ? (
              <Thumbnail
                source={displayImage.url}
                alt={displayTitle}
                size="extraSmall"
              />
            ) : null}
            <Text as="p" variant="headingMd" truncate>
              {displayTitle}
            </Text>
          </InlineStack>
        </BlockStack>
      </Box>
      <BlockStack>{results}</BlockStack>
    </Box>
  );
};
